<template>
  <p-container>
    <h2 class="mb-4">
      {{ $t('page.app.redebits.list.title') }}
    </h2>
    <p-request-alert v-model="page.redebitDataSource.error"></p-request-alert>
    <div v-if="!page.redebitDataSource.error">
      <p-row class="filter">
        <p-col>
          <p-text-field
            v-model="page.redebitDataSource.filter.searchCriteria"
            clearable
            :label="$t('page.app.redebits.list.filter.searchCriteria')"
            context-icon="search"
          ></p-text-field>
        </p-col>
        <p-col>
          <p-date-field
            v-model="page.redebitDataSource.filter.from"
            clearable
            :label="$t('page.app.redebits.list.filter.from')"
          ></p-date-field>
        </p-col>
        <p-col>
          <p-date-field
            v-model="page.redebitDataSource.filter.to"
            clearable
            :label="$t('page.app.redebits.list.filter.to')"
          ></p-date-field>
        </p-col>
        <p-col>
          <p-select
            v-model="page.redebitDataSource.filter.uploadStatusId"
            :label="$t('page.app.redebits.list.filter.uploadState')"
            :items="page.uploadStates"
            clearable
          ></p-select>
        </p-col>
        <p-col>
          <p-select
            v-model="page.redebitDataSource.filter.paymentStatus"
            :label="$t('page.app.redebits.list.filter.paymentState')"
            :items="page.paymentStates"
            clearable
          ></p-select>
        </p-col>
        <p-col>
          <p-select
            v-model="page.filterUploadExportStateId"
            :label="$t('page.app.redebits.list.filter.uploadExportState')"
            :items="page.exportStates"
            clearable
          >
          </p-select>
        </p-col>
        <p-col>
          <p-select
            v-model="page.filterPaymentExportStateId"
            :label="$t('page.app.redebits.list.filter.paymentExportState')"
            :items="page.exportStates"
            clearable
          >
          </p-select>
        </p-col>
      </p-row>
      <p-row>
        <p-col>
          <p-card>
            <p-row v-if="page.paymentDownloadMessage">
              <p-col>
                <p-alert v-model="page.paymentDownloadMessage.message" :type="page.paymentDownloadMessage.type" dismissable>
                  {{ page.paymentDownloadMessage.message }}
                </p-alert>
              </p-col>
            </p-row>
            <p-row>
              <p-col>
                <p-surface layer="2" class="pl-2">
                  <p-row align-center>
                    <p-col>
                      <p-button @click="page.redebitDataSource.select()">
                        <p-icon dense>
                          refresh
                        </p-icon>
                      </p-button>
                      <p-button
                        class="ml-4"
                        @click="$router.push('/_admin/redebits/upload')"
                      >
                        <p-icon>upload_file</p-icon>
                        {{ $t('page.app.redebits.list.uploadFile') }}
                      </p-button>
                    </p-col>
                    <p-col justify-end class="mr-4">
                      <p-button
                        :disabled="page.selectedRedebits.length < 1"
                        :loading="page.redebitDownloadUploadDataSource.isLoading"
                        @click="page.downloadUploadStatusReport()"
                      >
                        <p-icon>download</p-icon>
                        {{ $t('page.app.redebits.list.downloadUploadStatusReport') }}
                      </p-button>
                      <p-button
                        class="ml-4"
                        :disabled="page.selectedRedebits.length < 1"
                        :loading="page.redebitDownloadPaymentStatusExportDataSource.isLoading"
                        @click="page.downloadPaymentStatusExport()"
                      >
                        <p-icon>download</p-icon>
                        {{ $t('page.app.redebits.list.downloadPaymentStatusExport') }}
                      </p-button>
                    </p-col>
                  </p-row>
                </p-surface>
              </p-col>
            </p-row>
            <p-row>
              <p-col>
                <p-progress-linear v-if="page.redebitDataSource.isLoading || !page.redebitDataSource.data" :indeterminate="true" />
                <p-table v-else>
                  <template #rows>
                    <tr>
                      <th>
                        <p-checkbox
                          :value="page.selectedRedebits.length === page.redebitDataSource.data.items.length"
                          @input="page.toggleSelectAllRedebits()"
                        ></p-checkbox>
                      </th>
                      <th>{{ $t('page.app.redebits.list.header.date') }}</th>
                      <th>{{ $t('page.app.redebits.list.header.transactionId') }}</th>
                      <th>{{ $t('page.app.redebits.list.header.debtor') }}</th>
                      <th>{{ $t('page.app.redebits.list.header.amount') }}</th>
                      <th>{{ $t('page.app.redebits.list.header.uploadState') }}</th>
                      <th>{{ $t('page.app.redebits.list.header.uploadExportState') }}</th>
                      <th>{{ $t('page.app.redebits.list.header.paymentState') }}</th>
                      <th>{{ $t('page.app.redebits.list.header.paymentExportState') }}</th>
                      <th></th>
                    </tr>
                    <tr v-for="redebit in page.redebitDataSource.data.items" :key="redebit.data.id">
                      <td>
                        <p-checkbox
                          :value="page.isRedebitSelected(redebit.data.id)"
                          @input="page.redebitToggleSelection(redebit.data.id)"
                        ></p-checkbox>
                      </td>
                      <td>{{ $date(redebit.data.entryDate).format('DD.MM.YYYY HH:mm:ss') }}</td>
                      <td>{{ redebit.data.transactionId }}</td>
                      <td>{{ redebit.data.debtorName }}</td>
                      <td>
                        <currency-amount :amount="redebit.data.amount" :currency="redebit.data.currency" />
                      </td>
                      <td>{{ page.statusCodeToState(redebit.data.uploadStatus) }}</td>
                      <td>{{ redebit.data.uploadStatusReportDownloadDate === undefined ? $t('page.app.redebits.list.data.notExported') : $t('page.app.redebits.list.data.exported') }}</td>
                      <td>{{ page.translatePaymentState(redebit.data.paymentStatus) }}</td>
                      <td>{{ redebit.data.paymentStatusReportDownloadDate === undefined ? $t('page.app.redebits.list.data.notExported') : $t('page.app.redebits.list.data.exported') }}</td>
                      <td>
                        <p-button
                          class="mr-2"
                          :disabled="page.redebitDataSource.isLoading"
                          @click="$router.push(`/_admin/redebits/${redebit.data.id}`)"
                        >
                          <p-icon>find_in_page</p-icon>
                        </p-button>
                      </td>
                    </tr>
                  </template>
                </p-table>
                <p-pagination v-model="page.redebitDataSource.paging"></p-pagination>
              </p-col>
            </p-row>
          </p-card>
        </p-col>
      </p-row>
    </div>
  </p-container>
</template>

<script lang="ts">
  import Vue from 'vue';
  import RedebitListPage from './list.vue.model';

  export default Vue.extend({
    auth: true,
    role: ['admin', 'hostadmin'],
    layout: 'layout-hostadmin',
    data: () => ({
      page: new RedebitListPage(),
    }),
    async mounted() {
      await this.page.initialize();
    },
  });
</script>
